// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@plumberssupply/App/Handlers/XNG/Header";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@plumberssupply/Overrides/Components/DistanceDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@plumberssupply/Overrides/Components/FindLocationModal";
import WarehouseHoursLink from "@plumberssupply/Overrides/Components/WarehouseHoursLink";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { ChangeEvent, FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    // XNG-Changes
    displayContentInsideModal?: boolean;
    setPickUpWarehouseUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
}

const mapStateToProps = (state: CustomAppState) => ({
    session: state.context.session,
    accountSettings: getSettingsCollection(state).accountSettings,
    isMobile: getXngCommon(state).isMobileScreen,
});

const mapDispatchToProps = {
    setFulfillmentMethod,
    updatePickUpWarehouse,
    xHeaderUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ChangeCustomerFulfillmentMethodSelectorStyles {
    container?: GridContainerProps;
    radioGroupGridItem?: GridItemProps;
    fulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    fulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    pickupLocationGridItem?: GridItemProps;
    selectedWarehouseLink?: LinkPresentationProps;
    selectedWarehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    pickUpLocation?: PickUpLocationStyles;
    selectedWarehouseDetailsGridItem?: GridItemProps;
    selectedWarehouseLinksGridItem?: GridItemProps;
}

interface PickUpLocationStyles {
    wrapper?: GridContainerProps;
    headerText?: TypographyProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
}

interface WarehouseAddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    nameText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}

const smallText: TypographyProps = {
    size: 12,
    lineHeight: "15px",
};

export const changeCustomerFulfillmentMethodStyles: ChangeCustomerFulfillmentMethodSelectorStyles = {
    container: {
        css: css``,
    },
    radioGroupGridItem: {
        width: 12,
    },
    pickupLocationGridItem: {
        width: [12, 12, 6, 6, 7],
    },
    pickUpLocation: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
            `,
        },
        headerText: {
            css: css`
                margin-right: 20px;
                font-size: 14px;
                font-weight: 800;
                line-height: 18px;
                color: ${color("common.ui11")};
            `,
        },
        changeLink: {
            typographyProps: {
                size: 12,
            },
            css: css`
                margin-left: 15px;
            `,
        },
        warehouseAddress: {
            wrapper: {
                css: css`
                    margin-top: -3px;
                `,
            },
            nameText: {
                ...smallText,
                weight: "bold",
            },
            address: {
                customerNameText: { ...smallText },
                attentionText: { ...smallText },
                companyNameText: { ...smallText },
                address1Text: { ...smallText },
                address2Text: { ...smallText },
                address3Text: { ...smallText },
                address4Text: { ...smallText },
                cityStatePostalCodeText: { ...smallText },
                countryText: { ...smallText },
                phoneText: { ...smallText },
                emailText: { ...smallText },
                faxText: { ...smallText },
            },
        },
    },
    fulfillmentMethodRadioGroup: {
        labelProps: {
            lineHeight: "18px",
            color: "common.ui11",
            css: css`
                font-weight: 800;
                font-size: 14px;
            `,
        },
    },
    fulfillmentMethodRadio: {
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-top: 2px;
        `,
    },
    selectedWarehouseLink: {
        typographyProps: {
            size: 12,
            lineHeight: "15px",
        },
    },
    selectedWarehouseGoogleMapsDirectionLink: {
        typographyProps: {
            size: 12,
            lineHeight: "15px",
        },
    },
    selectedWarehouseDetailsGridItem: {
        width: 8,
        css: css`
            column-gap: 20px;
            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    selectedWarehouseLinksGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
            row-gap: 4px;
        `,
    },
};

const styles = changeCustomerFulfillmentMethodStyles;

const ChangeCustomerFulfillmentMethodSelector: FC<Props> = ({
    session,
    accountSettings,
    setFulfillmentMethod,
    updatePickUpWarehouse,
    isMobile,
    displayContentInsideModal,
    setPickUpWarehouseUpdated,
    xHeaderUpdate,
}) => {
    if (!accountSettings || !accountSettings.enableWarehousePickup) {
        return null;
    }

    const toasterContext = React.useContext(ToasterContext);
    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
        setPickUpWarehouseUpdated?.(true);
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        setPickUpWarehouseUpdated?.(true);
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    // XNG-Changes
    const componentStyles = styles.pickUpLocation || {};
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const handleFindLocationModalClose = () => {setIsFindLocationOpen(false);xHeaderUpdate({ key: "fulfillmentMethodModalOpen", value: false });}
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        handlePickUpAddressChange(warehouse);
        setIsFindLocationOpen(false);
        xHeaderUpdate({ key: "fulfillmentMethodModalOpen", value: false });
    };
    const [distanceUnitOfMeasure, setDistanceUnitOfMeasure] = React.useState<DistanceUnitOfMeasure>("Imperial");

    return (
        <GridContainer {...styles.container} gap={0}>
            <GridItem
                {...styles.radioGroupGridItem}
                width={session.fulfillmentMethod === FulfillmentMethod.Ship ? 12 : [12, 12, 6, 6, 5]}
            >
                <RadioGroup
                    label={`${translate("Fulfillment Method")}: `}
                    value={session.fulfillmentMethod}
                    onChangeHandler={fulfillmentMethodChangeHandler}
                    {...styles.fulfillmentMethodRadioGroup}
                    data-test-selector="changeCustomerFulfillmentMethod"
                    horizontal={true}
                >
                    <Radio
                        value={FulfillmentMethod.Ship}
                        {...styles.fulfillmentMethodRadio}
                        data-test-selector="fulfillmentMethod_ship"
                    >
                        {translate("Ship")}
                    </Radio>
                    <Radio
                        value={FulfillmentMethod.PickUp}
                        {...styles.fulfillmentMethodRadio}
                        data-test-selector="fulfillmentMethod_pickUp"
                    >
                        {translate("Pick Up")}
                    </Radio>
                </RadioGroup>
            </GridItem>
            {session.fulfillmentMethod === FulfillmentMethod.PickUp &&
                session.pickUpWarehouse &&
                (displayContentInsideModal ? (
                    <PickUpLocation warehouse={session.pickUpWarehouse} onChange={handlePickUpAddressChange} headerUpdate={xHeaderUpdate} />
                ) : (
                    <>
                        <GridItem
                            {...styles.pickupLocationGridItem}
                            style={{
                                marginTop: isMobile ? "14px" : 0,
                            }}
                        >
                            <GridContainer data-test-selector="changeCustomer_pickUpLocation">
                                <GridItem {...styles.selectedWarehouseDetailsGridItem}>
                                    <Typography {...componentStyles?.headerText}>
                                        {translate("My Location")}:
                                    </Typography>
                                    <WarehouseAddressInfoDisplay warehouse={session.pickUpWarehouse} />
                                </GridItem>
                                <GridItem {...styles.selectedWarehouseLinksGridItem}>
                                    {session.pickUpWarehouse?.hours && (
                                        <WarehouseHoursLink
                                            {...styles.selectedWarehouseLink}
                                            warehouse={session.pickUpWarehouse}
                                        />
                                    )}
                                    <GoogleMapsDirectionLink
                                        {...styles.selectedWarehouseGoogleMapsDirectionLink}
                                        {...session.pickUpWarehouse}
                                    />
                                    {session.pickUpWarehouse.phone && (
                                        <Link
                                            href={`tel:${session.pickUpWarehouse.phone}`}
                                            typographyProps={{
                                                size: 12,
                                                lineHeight: "15px",
                                            }}
                                            target="_blank"
                                        >
                                            {session.pickUpWarehouse.phone}
                                        </Link>
                                    )}
                                        <DistanceDisplay
                                            distance={session.pickUpWarehouse.distance}
                                            unitOfMeasure={distanceUnitOfMeasure}
                                        />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem
                            width={12}
                            css={css`
                                &&& {
                                    height: 1px;
                                    background: ${color("common.ui7")};
                                    margin: 20px 0;
                                }
                            `}
                        ></GridItem>
                        <GridItem width={12}>
                            <FindLocationModal
                                modalIsOpen={true}
                                onWarehouseSelected={handleWarehouseSelected}
                                onModalClose={handleFindLocationModalClose}
                                extendedStyles={componentStyles.findLocationModal}
                                displayContentInsideModal={displayContentInsideModal}
                                onDistanceUnitOfMeasureChange={(unit: DistanceUnitOfMeasure) => setDistanceUnitOfMeasure(unit)}
                            />
                        </GridItem>
                    </>
                ))}
        </GridContainer>
    );
};

interface PickUpLocationProps {
    warehouse: WarehouseModel;
    onChange: (address: WarehouseModel) => void;
    headerUpdate: any;
}

const PickUpLocation: FC<PickUpLocationProps> = ({ warehouse, onChange, headerUpdate }) => {
    const componentStyles = styles.pickUpLocation || {};
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => {setIsFindLocationOpen(false);headerUpdate({ key: "fulfillmentMethodModalOpen", value: false }); }
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        onChange(warehouse);
        setIsFindLocationOpen(false);
        headerUpdate({ key: "fulfillmentMethodModalOpen", value: false });
    };

    return (
        <>
            <StyledWrapper {...componentStyles.wrapper} data-test-selector="changeCustomer_pickUpLocation">
                <Typography {...componentStyles.headerText}>{translate("My Location")}:</Typography>
                <WarehouseAddressInfoDisplay warehouse={warehouse} />
                <Link
                    onClick={handleOpenFindLocation}
                    {...componentStyles.changeLink}
                    data-test-selector="changeCustomer_pickUpLocation_findLocation"
                >
                    {translate("Change")}
                </Link>
            </StyledWrapper>
            <FindLocationModal
                modalIsOpen={isFindLocationOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleFindLocationModalClose}
                extendedStyles={componentStyles.findLocationModal}
            />
        </>
    );
};

interface WarehouseAddressInfoDisplayProps {
    warehouse: WarehouseModel;
}

export const WarehouseAddressInfoDisplay: FC<WarehouseAddressInfoDisplayProps> = ({ warehouse }) => {
    const componentStyles =
        styles.pickUpLocation && styles.pickUpLocation.warehouseAddress ? styles.pickUpLocation.warehouseAddress : {};

    return (
        <StyledWrapper {...componentStyles.wrapper}>
            <Typography {...componentStyles.nameText} data-test-selector="changeCustomer_warehouseName">
                {warehouse.description || warehouse.name}
            </Typography>
            <AddressInfoDisplay
                address1={warehouse.address1}
                address2={warehouse.address2}
                city={warehouse.city}
                state={warehouse.state}
                postalCode={warehouse.postalCode}
                phone={warehouse.phone}
                extendedStyles={componentStyles.address}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerFulfillmentMethodSelector);

ChangeCustomerFulfillmentMethodSelector.defaultProps = {
    displayContentInsideModal: true,
};
